/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * Licensed under The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */
import { ComponentChild, ComponentProps, Fragment, Ref } from 'preact';
import { BaseButton } from '../UNSAFE_BaseButton';
import { ProgressButtonLabelLayout } from '../UNSAFE_ProgressButtonLabelLayout';
import { forwardRef, ElementRef } from 'preact/compat';
import type { DimensionProps } from '../utils/UNSAFE_interpolations/dimensions';
import { useTooltip } from '../hooks/UNSAFE_useTooltip';
import { TestIdProps } from '../hooks/UNSAFE_useTestId';
import type { ButtonIntrinsicProps } from '../utils/UNSAFE_buttonUtils';
import { processGlobals } from '../utils/UNSAFE_buttonUtils';
import { mergeProps } from '#utils/UNSAFE_mergeProps';
import translations from '#resources/nls/en/bundle';
import { LiveRegion } from '#UNSAFE_LiveRegion';
import { useProgressLoadingState } from '#hooks/PRIVATE_useProgressLoadingState';
type ButtonHandle = ElementRef<typeof BaseButton>;

type WidthInterpolationProps = Pick<DimensionProps, 'width'>;

type BaseProps = ButtonIntrinsicProps &
  WidthInterpolationProps &
  TestIdProps & {
    /**
     * Specifies the icon for the button.
     */
    children?: ComponentChild;

    /**
     * Specifies that a quick indeterminate action is taking place, making the button read-only.
     */
    isLoading?: boolean;

    /**
     * Specifies that the button element should be disabled.
     */
    isDisabled?: boolean;

    /**
     * Specifes the button type
     */
    type?: 'button' | 'submit';

    /**
     * Triggered when a button is clicked, whether by keyboard, mouse, or touch events.
     */
    onAction?: ComponentProps<typeof BaseButton>['onAction'];

    /**
     * Indicates in what states the button has chrome (background and border).
     *     "borderless"': "Borderless buttons are a more prominent variant. Borderless
     * buttons are useful for supplemental actions that require minimal emphasis.",
     *     "outlined"': "Outlined buttons are salient, but lighter weight than
     * solid buttons. Outlined buttons are useful for secondary actions.",
     *     "solid"': "Solid buttons stand out, and direct the user's attention to the
     *  most important actions in the UI.",
     *     "callToAction"': "A Call To Action (CTA) button guides the user to take or
     * complete the action that is the main goal of the page or page section. There
     * should only be one CTA button on a page at any given time.",
     */
    variant?: 'borderless' | 'outlined' | 'solid' | 'callToAction';
    /**
     * The size indicates how large the button is rendered.
     */
    size?: 'sm' | 'md' | 'lg';

    /**
     * tooltip - used for tooltip and aria-label
     */
    tooltip: string;
  };

/**
 * An Icon Progress button is an icon push button that can show progress.
 */
export const IconProgressButton = forwardRef(
  (
    {
      variant = 'outlined',
      isLoading = false,
      isDisabled = false,
      size = 'md',
      type: buttonType = 'button',
      onAction,
      width,
      tooltip,
      testId,
      ...props
    }: BaseProps & TestIdProps,
    ref: Ref<ButtonHandle>
  ) => {
    const { globalEventProps } = processGlobals(props);
    const isAdjustedLoading = isDisabled ? false : isLoading;

    const progressStatus = translations.accButtonProgressStatus({
      label: tooltip
    });
    const progressComplete = translations.accButtonProgressComplete({
      label: tooltip
    });
    const elementDetails = {
      type: 'button' as const,
      buttonType: buttonType
    };

    const tooltipLabel = tooltip;

    const { tooltipContent, tooltipProps } = useTooltip({
      text: tooltipLabel,
      isDisabled
    });
    const { loaded } = useProgressLoadingState(isAdjustedLoading);

    /* For a11y, we use ignore aria-label during loading time */
    const { 'aria-describedby': adb, ...ttProps } = tooltipProps;

    return (
      <Fragment>
        <BaseButton
          {...mergeProps(ttProps, globalEventProps)}
          ref={ref}
          elementDetails={elementDetails}
          isReadOnly={isAdjustedLoading}
          isDisabled={isDisabled}
          size={size}
          variant={variant}
          width={width}
          styling={isAdjustedLoading ? ['min', 'hiddenLabel', 'noHoverActive'] : ['min']}
          onAction={onAction}
          aria-label={tooltip}
          testId={testId}>
          <ProgressButtonLabelLayout
            display={'icons'}
            isLoading={isAdjustedLoading}
            isLoadingContrast={variant === 'callToAction'}
            startIcon={props.children}
            size={size}></ProgressButtonLabelLayout>
        </BaseButton>
        {tooltipContent}
        <LiveRegion type="assertive">
          {loaded ? progressComplete : isLoading ? progressStatus : ''}
        </LiveRegion>
      </Fragment>
    );
  }
);

IconProgressButton.displayName = 'IconProgressButton';
